import { batch, computed, signal, useComputed, useSignal, type Signal } from "@preact/signals";
import { render } from "preact";
import { useCallback, useEffect } from "preact/hooks";

import { type AllData, type DataPos, type LiveData, type LogData, type Pos } from "../src/data";

import DataPanel, { fmtNum } from "./data-panel";
import RenderAll from "./render-all";
import createCanvasRender from "./render-canvas";
import { RenderSvgOver, RenderSvgUnder } from "./render-svg";
import Selector from "./selector";
import useTheme from "./theme";

import { Show } from "@preact/signals/utils";
import "./style.css";

const logData = signal<Record<string, LogData>>({});
const liveData = signal<LiveData>({ type: "live", data: [] });
const liveDataUpdate = signal<Pos[]>([]);

const activeKey = signal<string>("");

const loggedData = computed(() => logData.value[activeKey.value]);

function Anchor(addr: number, x: number, y: number, z: number): Anchor {
	return {
		addr, x, y, z,
		visible: signal(true),
		distance: signal(-1),
		stale: signal(false),
	};
}

const anchors = [
	Anchor(0x03, 0.0, -0.010, 0.085),
	Anchor(0x00, 0.23, -0.3, 2.53),
	Anchor(0x04, 5.30, -0.08, 0.09),
	Anchor(0x01, 4.94, -0.46, 2.53),

	Anchor(0x07, 1.36, 4.58, 0.09),
	Anchor(0x06, 0.87, 4.54, 2.57),
	Anchor(0x02, 5.30, 4.46, 0.095),
	Anchor(0x08, 4.45, 4.54, 2.57),
];

const system = {
	anchors,
	anchorMap: new Map(anchors.map((a) => [a.addr, a])),
	anchorHovered: signal<number>(NaN),
	pos: signal({ x: 0, y: 0, z: 0 }),
};

export type System = typeof system;

export interface Anchor {
	addr: number,
	x: number,
	y: number,
	z: number,
	visible: Signal<boolean>,
	distance: Signal<number>,
	stale: Signal<boolean>,
}

async function fetchData() {
	const all = await fetch("/data.json").then((r) => r.json()) as AllData;
	batch(() => {
		liveData.value = all.live;
		logData.value = all.logs;

		const s = window.location.hash.slice(1);
		if (all.logs[s] != undefined) {
			activeKey.value = s;
		} else if (s == "live") {
			activeKey.value = s;
		} else {
			activeKey.value = Object.keys(all.logs)[0] ?? "live";
		}
	});
}

function useLiveWs() {
	const connected = useSignal(false);
	const ws = useSignal<WebSocket>();

	useEffect(() => {
		const _ws = new WebSocket("/");

		_ws.onopen = () => {
			connected.value = true;
		};

		_ws.onmessage = (e) => {
			try {
				const dp = JSON.parse(e.data as string) as DataPos;

				liveData.peek().data.push(dp);

				batch(() => {
					system.anchors.forEach((a) => {
						a.stale.value = true;
					});

					dp.distances.forEach((d) => {
						const a = system.anchorMap.get(d.addr);
						if (!a) {
							return;
						}
						a.distance.value = d.distance;
						a.stale.value = false;
					});

					system.pos.value = dp.pos;

					liveDataUpdate.value = [
						...liveDataUpdate.value,
						dp.pos,
					];
				});
			} catch (e) {
				console.error(e);
			}
		};

		_ws.onerror = (e) => {
			// FIXME: reconnecting
			connected.value = false;
			console.error(e);
		};

		ws.value = _ws;

		return () => {
			connected.value = false;
			_ws.close();
		};
	}, []);

	const clear = useCallback(() => {
		ws.peek()?.send(JSON.stringify({ action: "clear" }));
		liveData.value = {
			...liveData.value,
			data: [],
		};
	}, []);

	const save = useCallback(() => {
		const filename = prompt("dataset name");
		ws.peek()?.send(JSON.stringify({ action: "save", filename }));
		liveData.value = {
			...liveData.value,
			data: [],
		};
		setTimeout(() => void fetchData(), 1000);
	}, []);

	const setDist = useCallback(() => {
		const distance = parseFloat(prompt("distance (meters)") ?? "");
		if (isNaN(distance)) {
			console.error("distance is NaN");
			return;
		}
		ws.peek()?.send(JSON.stringify({ action: "setdist", distance: distance * 100 }));
	}, []);

	return { connected, clear, save, setDist };
}

const draw = createCanvasRender({
	liveData,
	loggedData,
	liveDataUpdate,
});

const theme = useTheme();

function Status({ wsConnected }: { wsConnected: Signal<boolean> }) {
	const isLive = useComputed(() => loggedData.value == undefined);

	return (
		<div class="status">
			<div>
				Websocket: {wsConnected.value ? <b style={{ color: theme.green }}>connected</b> : <b style={{ color: theme.red }}>disconnected</b>}
			</div>
			<Show when={isLive}>
				<div>
					Last position: {fmtNum(system.pos.value.x)}, {fmtNum(system.pos.value.y)}, {fmtNum(system.pos.value.z)}
				</div>
			</Show>
		</div>
	);
}

function App() {
	const ws = useLiveWs();
	const logDataOptions = useComputed(() => Object.keys(logData.value));
	const isLive = useComputed(() => activeKey.value == "live");

	return (
		<>
			<Selector keys={logDataOptions} active={activeKey} />
			<main>
				<RenderAll fromM={-1} toM={5.5} svgSize={20} draw={draw}>
					{{
						svgUnder: <RenderSvgUnder loggedData={loggedData} system={system} />,
						svgOver: <RenderSvgOver />,
					}}
				</RenderAll>
				<div class="sidebar">
					<DataPanel loggedData={loggedData} system={system} />
					<Show when={isLive}>
						<div>
							<Status wsConnected={ws.connected} />
							<button onClick={ws.clear}>Clear</button>
							<button onClick={ws.save}>Save</button>
							<button onClick={ws.setDist}>Set Distance</button>
						</div>
					</Show>
				</div>
			</main>
		</>
	);
}

const root = document.getElementById("root");
if (!root) {
	throw new Error("No <div id='root'> element found");
}
render(<App />, root);

fetchData().catch(console.error);
